import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Upload, Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Employees() {
  const [searchParams, setSearchParams] = useSearchParams();
  const outstandingOnly = searchParams.get("outstanding") === "1";
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openImport, setOpenImport] = useState(false);

  const load = async () => {
    const params = { q: q || undefined, branch: branchFilter, status: statusFilter };
    const { data } = await api.get("/employees", { params });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter, statusFilter]);
  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)); }, []);
  const doSearch = (e) => { e.preventDefault(); load(); };

  const visibleList = outstandingOnly ? list.filter(e => parseFloat(e.outstanding) > 0) : list;

  const openNew = () => { setEditing({ employee_id: "", name: "", branch_id: branches[0]?.id || "", monthly_salary: "", phone: "", status: "Active" }); setOpenForm(true); };
  const openEdit = (e) => { setEditing({ ...e }); setOpenForm(true); };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/employees/${editing.id}`, editing);
      else await api.post("/employees", editing);
      toast.success("Saved"); setOpenForm(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async (emp) => {
    try {
      const { data } = await api.delete(`/employees/${emp.id}`);
      const c = data.deleted;
      toast.success(`Deleted ${emp.name} (${c.advances} advances, ${c.repayments} repayments, ${c.bad_debts} bad debts also removed)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/employees/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = "employee_import_template.xlsx"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Directory</div>
          <h1 className="font-serif text-4xl mt-1">Employees</h1>
          {outstandingOnly && <div className="mt-2 flex items-center gap-2">
            <span className="pill pill-outstanding" data-testid="outstanding-filter-badge">Showing only employees with outstanding balances</span>
            <button className="text-sm gold hover:underline" onClick={() => setSearchParams({})} data-testid="clear-outstanding-filter">Clear</button>
          </div>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-2" />Template</Button>
          <Button variant="outline" onClick={() => setOpenImport(true)} data-testid="import-btn"><Upload className="w-4 h-4 mr-2" />Import Excel</Button>
          <Button className="gold-bg hover:opacity-90" onClick={openNew} data-testid="new-employee-btn"><Plus className="w-4 h-4 mr-2" />Add Employee</Button>
        </div>
      </div>
      <form onSubmit={doSearch} className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-64"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by ID or name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="employee-search" />
        </div>
        <Select value={branchFilter} onValueChange={setBranchFilter}><SelectTrigger className="w-48" data-testid="branch-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Resigned">Resigned</SelectItem></SelectContent>
        </Select>
        <Button type="submit" variant="outline">Search</Button>
      </form>
      <div className="border rounded-md overflow-x-auto"><table className="plutus"><thead><tr>
        <th>Employee ID</th><th>Name</th><th>Branch</th><th>Status</th><th className="text-right">Salary</th><th className="text-right">Outstanding</th><th></th>
      </tr></thead><tbody data-testid="employee-list">
        {visibleList.map(e => (
          <tr key={e.id}><td className="font-mono">{e.employee_id}</td><td>{e.name}</td><td>{e.branch_name}</td>
            <td><span className={`pill pill-${e.status.toLowerCase()}`}>{e.status}</span></td>
            <td className="text-right tabular-nums">{formatINR(e.monthly_salary)}</td>
            <td className="text-right tabular-nums gold">{formatINR(e.outstanding)}</td>
            <td className="text-right whitespace-nowrap">
              <Link to={`/employees/${e.id}`} className="text-sm gold hover:underline mr-3" data-testid={`view-${e.employee_id}`}>View</Link>
              <button className="text-sm text-muted-foreground hover:text-foreground mr-3" onClick={() => openEdit(e)} data-testid={`edit-${e.employee_id}`}>Edit</button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="text-sm text-red-400 hover:text-red-300" data-testid={`delete-${e.employee_id}`}><Trash2 className="w-3.5 h-3.5 inline" /></button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete {e.name}?</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently delete the employee <b>and all of their advances, repayments, bad debts, monthly deduction statuses and notifications</b>. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => doDelete(e)} className="bg-red-600 hover:bg-red-500 text-white" data-testid={`confirm-delete-${e.employee_id}`}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </td>
          </tr>
        ))}
        {visibleList.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">{outstandingOnly ? "No employees with outstanding balances." : "No employees yet."}</td></tr>}
      </tbody></table></div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="employee-form"><DialogHeader><DialogTitle>{editing?.id ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-2 gap-4">
            <div><Label>Employee ID *</Label><Input value={editing.employee_id} onChange={(e) => setEditing({ ...editing, employee_id: e.target.value })} data-testid="emp-id-input" /></div>
            <div><Label>Name *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="emp-name-input" /></div>
            <div><Label>Branch *</Label><Select value={editing.branch_id} onValueChange={(v) => setEditing({ ...editing, branch_id: v })}><SelectTrigger data-testid="emp-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Monthly Salary *</Label><Input type="number" value={editing.monthly_salary} onChange={(e) => setEditing({ ...editing, monthly_salary: e.target.value })} data-testid="emp-salary-input" /></div>
            <div><Label>Phone *</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="emp-phone-input" /></div>
            <div><Label>Status</Label><Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}><SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Resigned">Resigned</SelectItem></SelectContent></Select></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button><Button className="gold-bg" onClick={save} data-testid="save-emp-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog open={openImport} setOpen={setOpenImport} onDone={load} />
    </div>
  );
}

function ImportDialog({ open, setOpen, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/employees/import/preview", fd); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/employees/import/commit", { rows: preview.rows }); toast.success(`Imported ${data.inserted} employees`); setOpen(false); setPreview(null); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPreview(null); }}>
    <DialogContent className="max-w-4xl" data-testid="import-dialog"><DialogHeader><DialogTitle>Excel Import — Preview</DialogTitle></DialogHeader>
      {!preview ? <div><Input type="file" accept=".xlsx,.xls" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="import-file-input" />
        <p className="text-xs text-muted-foreground mt-2">Columns required: Employee ID, Employee Name, Branch, Monthly Salary, Phone Number, Employment Status.</p></div>
        : <div><div className="text-sm mb-3">Valid rows: <b className="text-green-500">{preview.valid_count}</b> · Errors: <b className="text-red-400">{preview.error_count}</b></div>
          <div className="max-h-96 overflow-auto border rounded"><table className="plutus"><thead><tr><th>Row</th><th>Employee ID</th><th>Name</th><th>Branch</th><th>Salary</th><th>Phone</th><th>Status</th><th>Errors</th></tr></thead>
            <tbody>{preview.rows.map(r => <tr key={r.row} className={r.valid ? "" : "bg-red-500/5"}>
              <td>{r.row}</td><td className="font-mono">{r.employee_id}</td><td>{r.name}</td><td>{r.branch_name}</td>
              <td className="text-right tabular-nums">{r.monthly_salary}</td><td>{r.phone}</td><td>{r.status}</td>
              <td className="text-red-400 text-xs">{r.errors.join(", ")}</td></tr>)}</tbody></table></div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setPreview(null)}>Choose another file</Button>
            <Button className="gold-bg" disabled={busy || preview.valid_count === 0} onClick={commit} data-testid="commit-import-btn">Commit {preview.valid_count} rows</Button></DialogFooter></div>}
    </DialogContent></Dialog>;
}
